import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Image } from '@codes/ui-kit/components/ui/image';
import { useMyRole } from '@web/app/shared/hooks';
import { useSelectedStore } from '@web/app/shared/stores';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { CodesLogoImage, LogoMiniDarkImage } from '@codes/assets';
import { useWorkspace } from '@codes/workspace';

import { getLightMenuItems, getMenuItems } from '../../consts';

export const SideBar = () => {
    const { workspace, project } = useSelectedStore();
    const { data: workspaceFromServer } = useWorkspace('0');

    const [expandedMenus, setExpandedMenus] = useState({});
    const [isHovered, setIsHovered] = useState(false);
    const isCollapsed = true;
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const myRole = useMyRole();

    const toggleSubmenu = index => {
        setExpandedMenus(prev => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const isMenuItemActive = item => {
        if (item.hasDropdown && item.subItems) {
            return item.subItems.some(subItem => location.pathname === subItem.href.split('?')[0]);
        }
        return location.pathname === item.href;
    };

    const isSubItemActive = href => {
        return location.pathname === href.split('?')[0];
    };

    const items = useMemo(() => {
        const menuItems = !project
            ? getLightMenuItems(workspaceFromServer?.id)
            : getMenuItems(workspaceFromServer?.id, project?.id);

        if (myRole > 1) {
            return menuItems.filter(item => !item.label.includes('settings'));
        }

        return menuItems;
    }, [workspaceFromServer?.id, project, myRole]);

    useEffect(() => {
        if (isCollapsed && !isHovered) {
            setExpandedMenus({});
        }
    }, [isCollapsed, isHovered]);

    if (!workspace) {
        return null;
    }

    return (
        <div
            className={`fixed top-0 left-6 bottom-0 z-[100] my-6 bg-white dark:bg-[#424242] border-[0.6px] border-gray-300 shadow-custom py-[22px] transition-all duration-300
        w-[58px] hover:w-[254px] rounded-[34px] px-3 flex flex-col max-md:hidden`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="relative h-[26px] mb-[17px] self-start w-full">
                <Image
                    className={`absolute top-0 left-1 h-[26px] object-contain transition-opacity duration-300 ${
                        isHovered ? 'opacity-0' : 'opacity-100'
                    }`}
                    src={LogoMiniDarkImage}
                />
                <Image
                    className={`absolute top-0 left-1 h-[26px] object-contain transition-opacity duration-300 ${
                        isHovered ? 'opacity-100' : 'opacity-0'
                    }`}
                    src={CodesLogoImage}
                />
            </div>
            <div className="flex-1 flex flex-col space-y-2">
                {items.map((item, index) => {
                    const isActive = isMenuItemActive(item);
                    const isExpanded = expandedMenus[index];
                    const showSubmenu = item.hasDropdown && isExpanded && (!isCollapsed || (isCollapsed && isHovered));

                    return (
                        <div key={index}>
                            <div
                                className={`group flex items-center justify-center gap-2 p-[6px] cursor-pointer h-[34px] duration-200 transition-colors hover:bg-[#FFF9F4] hover:dark:bg-[#301905] ${
                                    isActive ? 'bg-[#FFF9F4] dark:bg-[#301905]' : ''
                                } ${
                                    showSubmenu
                                        ? 'rounded-t-md rounded-b-none bg-[#FFF9F4] dark:bg-[#301905]'
                                        : 'rounded-md'
                                }`}
                                onClick={() => {
                                    if (item.hasDropdown) {
                                        toggleSubmenu(index);
                                    } else if (item.href) {
                                        navigate(item.href);
                                    }
                                }}
                            >
                                <div
                                    className={`shrink-0 ${
                                        item.noIconOpacity
                                            ? ''
                                            : isActive
                                            ? 'opacity-100'
                                            : 'opacity-54 group-hover:opacity-100'
                                    }`}
                                >
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <div
                                    className={`flex items-center flex-1 overflow-hidden transition-all duration-200 ease-in-out ${
                                        isCollapsed && !isHovered ? 'opacity-0 hidden' : 'opacity-100'
                                    }`}
                                >
                                    <span
                                        className={`flex-1 text-base font-medium  whitespace-nowrap ${
                                            isActive ? 'opacity-100' : 'opacity-54 group-hover:opacity-100'
                                        }`}
                                    >
                                        {t(item.label)}
                                    </span>
                                    {item.hasDropdown && (
                                        <div className="transition-transform duration-200 ease-in-out">
                                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {showSubmenu && (
                                <div className="bg-[#FFF9F4] dark:bg-[#301905] rounded-b-md py-2 px-3 space-y-[10px]">
                                    {item.subItems?.map((subItem, subIndex) => (
                                        <div
                                            key={subIndex}
                                            className={`cursor-pointer ${
                                                subIndex === (item.subItems?.length || 1) - 1 ? 'rounded-b-md' : ''
                                            }`}
                                            onClick={() => {
                                                navigate(subItem?.href);
                                            }}
                                        >
                                            <span
                                                className={`text-[15px] flex-1 text-gray-700 hover:text-point ${
                                                    isSubItemActive(subItem.href) ? 'text-point' : 'text-gray-700'
                                                }`}
                                            >
                                                {t(subItem.label)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
